import styled from 'styled-components'
import { omniaTheme } from '../../theme/omniaTheme'

// ============================================================
// Page-level wrapper
// ============================================================

export const StoryContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 14mm 14mm 14mm 14mm;
  box-sizing: border-box;

  @media print {
    width: 100% !important;
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    padding: 14mm 14mm 14mm 14mm !important;
  }
`

// ============================================================
// Two-column layout
// ============================================================

export const StoryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  flex: 1;
  min-height: 0;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 32px !important;
    flex: 1 !important;
  }
`

export const StoryLeftColumn = styled.div`
  display: flex;
  flex-direction: column;

  @media print {
    display: flex !important;
    flex-direction: column !important;
  }
`

/**
 * Right column: 1 large main photo on top (flex 2) + 2 small detail photos
 * in a side-by-side row below (flex 1). Mirrors the Property Showcase
 * MainPhoto / SecondaryPhotos pattern.
 */
export const StoryRightColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;

  @media print {
    display: flex !important;
    flex-direction: column !important;
    gap: 16px !important;
  }
`

export const StoryDetailRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  flex: 1;
  min-height: 0;

  @media print {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 16px !important;
    flex: 1 !important;
  }
`

// ============================================================
// Left column — text blocks
// ============================================================

export const StoryIntro = styled.p`
  font-family: ${omniaTheme.fonts.body};
  font-size: 16px;
  font-weight: ${omniaTheme.fontWeights.medium};
  color: ${omniaTheme.colors.black};
  line-height: 1.4;
  letter-spacing: ${omniaTheme.letterSpacing.tight};
  margin: 0 0 28px 0;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 16px !important;
    font-weight: ${omniaTheme.fontWeights.medium} !important;
    color: ${omniaTheme.colors.black} !important;
    line-height: 1.4 !important;
    margin: 0 0 28px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// Use div (not p) so we can host inner <p> from sanitized API HTML
// without producing invalid nested-paragraph markup.
export const StoryDescription = styled.div`
  font-family: ${omniaTheme.fonts.body};
  font-size: 12px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;
  margin: 0 0 28px 0;

  /* Style nested HTML emitted by DOMPurify */
  & > p,
  p {
    margin: 0 0 12px 0;
  }
  & > p:last-child,
  p:last-child {
    margin-bottom: 0;
  }
  strong,
  b {
    font-weight: ${omniaTheme.fontWeights.medium};
    color: ${omniaTheme.colors.black};
  }
  em,
  i {
    font-style: italic;
  }

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 12px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    margin: 0 0 28px 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Story location callout (left column bottom)
// 3px purple vertical bar on the left + 3 paragraphs.
// ============================================================

export const StoryLocationCallout = styled.div`
  border-left: 3px solid ${omniaTheme.colors.primary};
  padding-left: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  font-family: ${omniaTheme.fonts.body};
  font-size: 12px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;

  /* Sanitized inner HTML from API — style any inner paragraphs/marks */
  p {
    margin: 0 0 12px 0;
    font-size: inherit;
    color: inherit;
    line-height: inherit;
  }
  p:last-child {
    margin-bottom: 0;
  }
  strong,
  b {
    font-weight: ${omniaTheme.fontWeights.medium};
    color: ${omniaTheme.colors.black};
  }

  @media print {
    border-left: 3px solid ${omniaTheme.colors.primary} !important;
    padding-left: 16px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 12px !important;
    font-size: 11px !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const StoryLocationParagraph = styled.p`
  font-family: ${omniaTheme.fonts.body};
  font-size: 12px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: ${omniaTheme.colors.text};
  line-height: 1.5;
  margin: 0;

  @media print {
    font-family: ${omniaTheme.fonts.body} !important;
    font-size: 12px !important;
    font-weight: ${omniaTheme.fontWeights.light} !important;
    color: ${omniaTheme.colors.text} !important;
    line-height: 1.5 !important;
    margin: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

// ============================================================
// Right column — photos
// ============================================================

export const StoryPhotoMain = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  flex: 2;
  min-height: 0;
  background: ${({ $imageUrl }) =>
    $imageUrl ? `url('${$imageUrl}') center/cover` : '#C9D6DE'};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 0;

  @media print {
    width: 100% !important;
    flex: 2 !important;
    background: ${({ $imageUrl }) =>
      $imageUrl
        ? `url('${$imageUrl}') center/cover !important`
        : 'transparent !important'};
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: 0 !important;
    border-radius: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const StoryPhotoDetail = styled.div<{ $imageUrl?: string }>`
  width: 100%;
  height: 100%;
  min-height: 0;
  background: ${({ $imageUrl }) =>
    $imageUrl ? `url('${$imageUrl}') center/cover` : '#9DB3A0'};
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 0;

  @media print {
    width: 100% !important;
    height: 100% !important;
    background: ${({ $imageUrl }) =>
      $imageUrl
        ? `url('${$imageUrl}') center/cover !important`
        : 'transparent !important'};
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: 0 !important;
    border-radius: 0 !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
`

export const StoryPhotoPlaceholder = styled.span`
  font-family: ${omniaTheme.fonts.body};
  font-size: 11px;
  font-weight: ${omniaTheme.fontWeights.light};
  color: #5f6e55;

  /* Hidden in the PDF — empty photo slots stay blank, no "[ Image ]" label. */
  @media print {
    display: none !important;
  }
`
